import React, { useRef, useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text, Button } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { WebView } from 'react-native-webview';

const SIGNATURE_HTML = `
<!DOCTYPE html>
<html>
<head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body {
      width: 100%;
      height: 100%;
      background: #fff;
      overflow: hidden;
      touch-action: none;
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      user-select: none;
      overscroll-behavior: none;
    }
    canvas {
      display: block;
      width: 100%;
      height: 100%;
      cursor: crosshair;
      touch-action: none;
    }
  </style>
</head>
<body>
  <canvas id="signatureCanvas"></canvas>
  <script>
    const canvas = document.getElementById('signatureCanvas');
    const ctx = canvas.getContext('2d');
    let isDrawing = false;
    let hasSignature = false;

    function resizeCanvas() {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const dpr = window.devicePixelRatio || 1;
      // Preservar el trazo actual antes de redimensionar (cambiar canvas.width
      // borra el contenido). Lo recuperamos escalándolo al nuevo tamaño.
      let snapshot = null;
      if (canvas.width > 0 && canvas.height > 0 && hasSignature) {
        try { snapshot = canvas.toDataURL('image/png'); } catch (e) {}
      }
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      if (snapshot) {
        const img = new Image();
        img.onload = function() { ctx.drawImage(img, 0, 0, rect.width, rect.height); };
        img.src = snapshot;
      }
    }

    resizeCanvas();
    // Algunas WebViews terminan de acomodar el layout después del primer paint;
    // re-ajustamos un par de veces para evitar quedar con tamaño viejo.
    setTimeout(resizeCanvas, 50);
    setTimeout(resizeCanvas, 250);
    window.addEventListener('resize', resizeCanvas);
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => resizeCanvas());
      ro.observe(canvas);
    }

    function getPos(e) {
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches ? e.touches[0] : e;
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
    }

    function startDrawing(e) {
      e.preventDefault();
      e.stopPropagation();
      isDrawing = true;
      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      try { window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'drawStart' })); } catch (e) {}
    }

    function draw(e) {
      e.preventDefault();
      e.stopPropagation();
      if (!isDrawing) return;
      hasSignature = true;
      const pos = getPos(e);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    }

    function stopDrawing(e) {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      if (isDrawing) {
        isDrawing = false;
        try { window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'drawEnd' })); } catch (e) {}
      }
    }

    canvas.addEventListener('touchstart', startDrawing, { passive: false });
    canvas.addEventListener('touchmove', draw, { passive: false });
    canvas.addEventListener('touchend', stopDrawing, { passive: false });
    canvas.addEventListener('touchcancel', stopDrawing, { passive: false });
    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseleave', stopDrawing);

    // API expuesta a React Native via injectJavaScript
    window.clearSignature = function() {
      const dpr = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
      hasSignature = false;
    };

    window.getSignature = function() {
      if (!hasSignature) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'empty' }));
        return;
      }
      const dataUrl = canvas.toDataURL('image/png');
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'signature',
        data: dataUrl
      }));
    };
  </script>
</body>
</html>
`;

const SignaturePad = React.forwardRef(({ onSignature, onClear, onDrawingChange, height }, ref) => {
  const webViewRef = useRef(null);
  const padHeight = height || 200;

  React.useImperativeHandle(ref, () => ({
    getSignature: () => {
      webViewRef.current?.injectJavaScript('window.getSignature(); true;');
    },
    clear: () => {
      webViewRef.current?.injectJavaScript('window.clearSignature(); true;');
      if (onClear) onClear();
    },
  }));

  const handleMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.nativeEvent.data);
      if (data.type === 'signature' && onSignature) {
        onSignature(data.data);
      } else if (data.type === 'empty' && onSignature) {
        onSignature(null);
      } else if (data.type === 'drawStart' && onDrawingChange) {
        onDrawingChange(true);
      } else if (data.type === 'drawEnd' && onDrawingChange) {
        onDrawingChange(false);
      }
    } catch (error) {
      console.error('Error parsing signature data:', error);
    }
  }, [onSignature, onDrawingChange]);

  const handleClear = useCallback(() => {
    webViewRef.current?.injectJavaScript('window.clearSignature(); true;');
    if (onClear) onClear();
  }, [onClear]);

  // El padre debe pasar scrollEnabled={!firmando} usando onDrawingChange
  // para evitar que el ScrollView se lleve el touch durante el dibujo.
  // No reclamamos el responder aquí porque eso captura *todos* los gestos
  // (incluido el del botón "Limpiar" y el touchmove del canvas, dejando
  // únicamente puntitos en lugar de trazos).
  return (
    <View style={[styles.container, { height: padHeight }]}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Ionicons name="pencil" size={16} color="#666" />
          <Text style={styles.label}>Firma del cliente</Text>
        </View>
        <Button
          mode="text"
          onPress={handleClear}
          compact
          labelStyle={styles.clearBtnText}
        >
          Limpiar
        </Button>
      </View>

      <View style={[styles.canvasContainer, { height: padHeight - 40 }]}>
        <WebView
          ref={webViewRef}
          source={{ html: SIGNATURE_HTML }}
          style={styles.webview}
          scrollEnabled={false}
          bounces={false}
          onMessage={handleMessage}
          javaScriptEnabled={true}
          originWhitelist={['*']}
          androidLayerType="software"
          nestedScrollEnabled={false}
          overScrollMode="never"
          mixedContentMode="always"
        />
        <View style={styles.signLine} pointerEvents="none">
          <View style={styles.line} />
          <Text style={styles.signText}>Firme aquí</Text>
        </View>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#DDD',
    backgroundColor: '#fff',
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 12,
    height: 40,
    backgroundColor: '#F8F9FA',
    borderBottomWidth: 1,
    borderBottomColor: '#EEE',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  label: {
    fontSize: 13,
    color: '#666',
    marginLeft: 6,
    fontWeight: '500',
  },
  clearBtnText: {
    fontSize: 12,
    color: '#DC3545',
  },
  canvasContainer: {
    position: 'relative',
  },
  webview: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  signLine: {
    position: 'absolute',
    bottom: 30,
    left: 20,
    right: 20,
    alignItems: 'center',
  },
  line: {
    width: '100%',
    height: 1,
    backgroundColor: '#CCC',
  },
  signText: {
    fontSize: 10,
    color: '#BBB',
    marginTop: 4,
  },
});

export default SignaturePad;
